import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import {
  CreateNetworkProgramInput,
  CreateNetworkProgramMutation,
  CreateNetworkProgramMutationVariables,
  DeleteNetworkProgramInput,
  DeleteNetworkProgramMutation,
  DeleteNetworkProgramMutationVariables,
  ListNetworkProgramsQuery,
  ListNetworkProgramsQueryVariables,
  ListNetworksQuery,
  ModelNetworkConnection,
  ModelNetworkProgramConnection,
  Network,
  NetworkProgram
} from "../../API";
import { API } from "aws-amplify";
import { genericQuery, fetchAllWithPagination, listQueryAutoPaginated } from "../api/apiUtils";

// Shape sent to the network-create / network-update REST lambdas. Keys mirror
// the lambda request types (and the DB columns), so `dashboardUrl` is lower-case
// here even though the generated `Network` type exposes it as `dashboardURL`.
export type NetworkWriteInput = {
  name: string;
  sponsorId: string;
  acgmeEnabled: boolean | null;
  allAttendingHistoryDisabled: boolean | null;
  simpl1Id: number | null;
  dashboardUrl: string | null;
  showDashboardLink: boolean | null;
  traineeCanRateTrainee: boolean | null;
  feedbackMinWords: number | null;
};

export const fetchAllNetworks = async (): Promise<Network[] | null> => {
  return await fetchAllWithPagination<Network>("networks", "networks", 500);
};

// The network view/edit pages don't use the linked-program list, so they fetch
// with includePrograms=false to skip fetching hundreds of programs per load.
export const fetchNetwork = async (id: string, includePrograms = true) =>
  (await API.get(
    "simpl-api",
    `network/${id}`,
    includePrograms ? {} : { queryStringParameters: { programs: "false" } }
  )) as Network;

// SE-490 — resolved per-network identifier config for a program, grouped by
// role. Backed by the program-identifier-config lambda (GET
// /program/{id}/identifier-config). The create/edit/bulk forms read from this
// to know which identifier fields to render and whether each is required.
export type IdentifierConfigItem = {
  key: string;
  displayName: string;
  validationRegex: string | null;
  required: boolean;
  infoUrl: string | null;
  rationaleLabel: string | null;
};

export type ProgramIdentifierConfig = {
  programId: string;
  networks: { id: string; name: string }[];
  identifiersByRole: Record<string, IdentifierConfigItem[]>;
};

export const fetchProgramIdentifierConfig = async (
  programId: string
): Promise<ProgramIdentifierConfig> =>
  (await API.get(
    "simpl-api",
    `program/${programId}/identifier-config`,
    {}
  )) as ProgramIdentifierConfig;

// SE-3118 — every identifier type in the system, in the order they were added
// (GET /identifiers). Drives the SysAdmin bulk template and tells identifier
// columns apart from other columns on bulk upload.
export type IdentifierType = {
  key: string;
  displayName: string;
  validationRegex: string | null;
};

export const fetchIdentifierTypes = async (): Promise<IdentifierType[]> =>
  (await API.get("simpl-api", "identifiers", {})) as IdentifierType[];

// SE-2251 — per-network identifier config for the Networks admin UI.
// Backed by GET/PUT /network/{id}/identifier-config.
export type NetworkIdentifierRoleConfig = {
  programRoleId: string;
  programRole: string;
  required: boolean;
};

export type NetworkIdentifierConfigItem = {
  identifierId: string;
  key: string;
  displayName: string;
  roles: NetworkIdentifierRoleConfig[];
  userCount: number;
};

export type NetworkIdentifierConfigResponse = {
  availableIdentifiers: { id: string; key: string; displayName: string }[];
  roles: { id: string; name: string }[];
  config: NetworkIdentifierConfigItem[];
};

export type NetworkIdentifierConfigWriteItem = {
  identifierId: string;
  roles: { programRoleId: string; required: boolean }[];
};

export const fetchNetworkIdentifierConfig = async (
  networkId: string
): Promise<NetworkIdentifierConfigResponse> =>
  (await API.get(
    "simpl-api",
    `network/${networkId}/identifier-config`,
    {}
  )) as NetworkIdentifierConfigResponse;

export const updateNetworkIdentifierConfig = async (
  networkId: string,
  items: NetworkIdentifierConfigWriteItem[]
) =>
  (await API.put("simpl-api", `network/${networkId}/identifier-config`, {
    body: { items }
  })) as { success: boolean; config: NetworkIdentifierConfigItem[] };

export const createNetwork = async (input: NetworkWriteInput) =>
  (await API.put("simpl-api", "network", { body: input })) as { id: string };

export const updateNetwork = async (id: string, input: NetworkWriteInput) =>
  (await API.put("simpl-api", `network/${id}`, { body: input })) as {
    result: { id: string };
  };

export const fetchNetworkProgramsByPrograms = async (programIds: string[]) => {
  const predicate: ListNetworkProgramsQueryVariables = {
    filter: {
      or: programIds.map((programId) => ({ programID: { eq: programId } }))
    }
  };
  return await listQueryAutoPaginated<
    ListNetworkProgramsQuery,
    NetworkProgram,
    ModelNetworkProgramConnection
  >(queries.listNetworkPrograms, "listNetworkPrograms", "items", predicate);
};

export async function deleteNetworkProgram(id: string) {
  const input: DeleteNetworkProgramInput = {
    id: id
  };
  const variables: DeleteNetworkProgramMutationVariables = {
    input: input
  };
  return await genericQuery<DeleteNetworkProgramMutation, NetworkProgram>(
    mutations.deleteNetworkProgram,
    "deleteNetworkProgram",
    variables
  );
}

export async function createNetworkProgram(
  programId: string,
  networkId: string
) {
  const input: CreateNetworkProgramInput = {
    programID: programId,
    networkID: networkId
  };
  const variables: CreateNetworkProgramMutationVariables = {
    input: input
  };
  //
  // return await genericQuery<CreateNetworkProgramMutation, NetworkProgram>(
  //   mutations.createNetworkProgram,
  //   "createNetworkProgram",
  //   variables
  // );

  console.error("createNetworkProgram is deprecated. Network associations are now created via the program-create endpoint. Do not call this function.");
}
